<script setup lang="ts">
import type { Size } from '@/types';
import { computed, ref, watch } from "vue";
import { useGlobalStyles } from '@/useGlobalStyles.ts';
import { Icon } from "@iconify/vue";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue'
import countriesData from '../../commons/countries.json'

interface Country {
  flags: {
    svg: string
    png: string
    alt: string
  }
  name: {
    common: string
    official: string
  }
  idd: {
    root: string
    suffixes: string[]
  }
}

interface CountryOption {
  code: string
  name: string
  flag: string
  displayCode: string
}

interface Props {
  label?: string
  icon?: string
  size?: Size
  rounded?: Size
  required?: boolean
  modelValue?: string
  placeholder?: string
  disabled?: boolean
  defaultCountry?: string
}

const props = withDefaults(defineProps<Props>(), {
  icon: 'material-symbols:phone',
  size: 'base',
  rounded: 'base',
  required: false,
  modelValue: '',
  placeholder: "Enter phone number",
  disabled: false,
  defaultCountry: '+1'
})

const emit = defineEmits(['update:modelValue'])

const { roundedClasses, textSizes } = useGlobalStyles()

const query = ref('')
const phoneNumber = ref('')
const selectedCountry = ref<CountryOption | null>(null)
const iconSize = ref<string>("w-5 h-5")
const isFocused = ref<boolean>(false)
const isDropdownOpen = ref<boolean>(false)

// Max national number length by dial code (digits after country code)
// Covers all countries; fallback uses E.164 max (15 - country code digits)
const maxLengthByCode: Record<string, number> = {
  '1':10, '7':10, '20':10, '27':9, '30':10, '31':9, '32':9, '33':9, '34':9,
  '36':9, '39':10, '40':9, '41':9, '43':13, '44':10, '45':8, '46':9, '47':8,
  '48':9, '49':11, '51':9, '52':10, '53':8, '54':10, '55':11, '56':9, '57':10,
  '58':10, '60':10, '61':9, '62':12, '63':10, '64':9, '65':8, '66':9, '81':10,
  '82':10, '84':9, '86':11, '90':10, '91':10, '92':10, '93':9, '94':9, '95':10,
  '211':9, '212':9, '213':9, '216':8, '218':9, '220':7, '221':9, '222':8,
  '223':8, '224':9, '225':10, '226':8, '227':8, '228':8, '229':8, '230':8,
  '231':8, '232':8, '233':9, '234':10, '235':8, '236':8, '237':9, '238':7,
  '239':7, '240':9, '241':7, '242':9, '243':9, '244':9, '245':7, '248':7,
  '249':9, '250':9, '251':9, '252':8, '253':8, '254':9, '255':9, '256':9,
  '257':8, '258':9, '260':9, '261':9, '262':9, '263':9, '264':9, '265':9,
  '266':8, '267':8, '268':8, '269':7, '291':7, '297':7, '298':6, '299':6,
  '350':8, '351':9, '352':9, '353':9, '354':7, '355':9, '356':8, '357':8,
  '358':10, '359':9, '370':8, '371':8, '372':8, '373':8, '374':8, '375':10,
  '376':9, '377':8, '378':10, '380':9, '381':9, '382':8, '383':8, '385':9,
  '386':8, '387':8, '389':8, '420':9, '421':9, '423':7, '500':5, '501':7,
  '502':8, '503':8, '504':8, '505':8, '506':8, '507':8, '509':8, '590':9,
  '591':8, '592':7, '593':9, '594':9, '595':9, '596':9, '597':7, '598':8,
  '670':8, '673':7, '674':7, '675':8, '676':7, '677':7, '678':7, '679':7,
  '680':7, '685':7, '686':8, '687':6, '688':6, '689':6, '691':7, '692':7,
  '850':10, '852':8, '853':8, '855':9, '856':10, '880':10, '886':9, '960':7,
  '961':8, '962':9, '963':9, '964':10, '965':8, '966':9, '967':9, '968':8,
  '970':9, '971':9, '972':9, '973':8, '974':8, '975':8, '976':8, '977':10,
  '992':9, '993':8, '994':9, '995':9, '996':9, '998':9,
}

const getMaxLength = (countryCode: string): number => {
  const digits = countryCode.replace(/\D/g, '')
  if (maxLengthByCode[digits]) return maxLengthByCode[digits]
  // E.164 fallback: 15 total digits minus country code digits
  return 15 - digits.length
}

// Process countries data
const countryOptions = computed<CountryOption[]>(() => {
  const options: CountryOption[] = []

  countriesData.forEach((country: Country) => {
    if (country.idd?.root) {
      // If country has more than one suffix, only use the root to avoid duplicates
      if (country.idd.suffixes && country.idd.suffixes.length > 1) {
        options.push({
          code: country.idd.root,
          name: country.name.common,
          flag: country.flags.svg,
          displayCode: country.idd.root
        })
      }
      // If country has exactly one suffix, combine root + suffix
      else if (country.idd.suffixes && country.idd.suffixes.length === 1) {
        const code = `${country.idd.root}${country.idd.suffixes[0]}`
        options.push({
          code: code,
          name: country.name.common,
          flag: country.flags.svg,
          displayCode: code
        })
      }
      // If country has no suffixes, just use the root
      else {
        options.push({
          code: country.idd.root,
          name: country.name.common,
          flag: country.flags.svg,
          displayCode: country.idd.root
        })
      }
    }
  })

  // Sort alphabetically by country name
  return options.sort((a, b) => a.name.localeCompare(b.name))
})

// Filter countries based on search query
const filteredCountries = computed(() => {
  if (!query.value) {
    return countryOptions.value
  }

  const searchTerm = query.value.toLowerCase()
  return countryOptions.value.filter((country) => {
    return country.name.toLowerCase().includes(searchTerm) ||
           country.code.includes(searchTerm)
  })
})

// Initialize with default country or parse modelValue
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    // Try to parse the country code and phone number
    // Find the longest matching country code to avoid partial matches (e.g., +2 vs +256)
    const matchingCountries = countryOptions.value.filter(c => newValue.startsWith(c.code))
    const country = matchingCountries.sort((a, b) => b.code.length - a.code.length)[0]

    if (country) {
      selectedCountry.value = country
      const raw = newValue.substring(country.code.length).trim()
      const sanitized = raw.replace(/\D/g, '').replace(/^0+/, '')
      phoneNumber.value = sanitized.slice(0, getMaxLength(country.code))
    } else {
      phoneNumber.value = newValue.replace(/\D/g, '').replace(/^0+/, '')
    }
  } else {
    phoneNumber.value = ''
  }
}, { immediate: true })

// Set default country on mount if no modelValue
watch([countryOptions, () => props.defaultCountry], ([options, defaultCountry]) => {
  if (options.length > 0 && !props.modelValue) {
    const defaultOption = options.find(c => c.code === defaultCountry)
    if (defaultOption && !selectedCountry.value) {
      selectedCountry.value = defaultOption
    } else if (!selectedCountry.value) {
      // Fallback to first option if defaultCountry not found
      selectedCountry.value = options[0] ?? null
    }
  }
}, { immediate: true })

// Trim phone number if it exceeds new country's max length
watch(selectedCountry, (country) => {
  if (country && phoneNumber.value) {
    const max = getMaxLength(country.code)
    if (phoneNumber.value.length > max) {
      phoneNumber.value = phoneNumber.value.slice(0, max)
    }
  }
})

// Emit the complete phone number when either part changes
watch([selectedCountry, phoneNumber], ([country, number]) => {
  if (country && number) {
    emit('update:modelValue', `${country.code}${number}`)
  } else if (country && !number) {
    emit('update:modelValue', country.code)
  } else {
    emit('update:modelValue', '')
  }
})

const sizeClasses = (size: Size) => {
  switch (size) {
    case "xs":
      iconSize.value = "w-3 h-3"
      return "px-2 py-1 text-xs"
    case "sm":
      iconSize.value = "w-4 h-4"
      return "px-2 py-1 text-sm"
    case "md":
      iconSize.value = "w-5 h-5"
      return "px-3 py-2 text-md"
    case "lg":
      iconSize.value = "w-6 h-6"
      return "px-4 py-2 text-lg"
    case "xl":
      iconSize.value = "w-8 h-8"
      return "px-5 py-3 text-2xl"
    default:
      iconSize.value = "w-5 h-5"
      return "px-4 py-2 text-base"
  }
}

const wrapperClass = computed<string>(() => {
  let wrapper = 'flex items-center gap-2 w-full border border-gray-300 text-gray-600'

  if(props.rounded){
    wrapper += ' ' + roundedClasses(props.rounded)
  }

  if(props.size){
    wrapper += ' ' + sizeClasses(props.size)
  }

  if (isFocused.value || isDropdownOpen.value) {
    wrapper += " shadow-sm";
  }

  if (props.disabled) {
    wrapper += " bg-gray-100 cursor-not-allowed opacity-60";
  }

  return wrapper;
})

const inputClass = computed<string>(() => {
  return 'font-primary flex-1 focus:outline-none bg-transparent';
})

const optionsClass = computed<string>(() => {
  let classes = 'absolute mt-1 max-h-60 w-auto min-w-[300px] overflow-auto bg-white border border-gray-300 shadow-lg z-50'

  if(props.rounded){
    const optionsRounded = props.rounded === 'xl' ? 'lg' : props.rounded
    classes += ' ' + roundedClasses(optionsRounded)
  }

  return classes
})

const displayValue = (country: unknown) => {
  return (country as CountryOption)?.code || ''
}

const handlePhoneInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  // Strip non-digit characters and leading zeros
  let sanitized = target.value.replace(/\D/g, '').replace(/^0+/, '')
  // Enforce max length based on selected country
  if (selectedCountry.value) {
    const max = getMaxLength(selectedCountry.value.code)
    sanitized = sanitized.slice(0, max)
  }
  phoneNumber.value = sanitized
  target.value = sanitized
}
</script>

<template>
<div class="w-full flex flex-col gap-1">
  <label
    :class="textSizes(props.size)"
    class="text-gray-600"
    v-if="props.label"
  >
    {{ props.label }}
    <span v-if="props.required" class="text-red-500">*</span>
  </label>

  <div :class="wrapperClass">
    <!-- Leading Icon (optional) -->
    <Icon
      v-if="props.icon"
      :icon="props.icon"
      :class="iconSize"
    />

    <!-- Country Code Dropdown -->
    <Combobox
      v-model="selectedCountry"
      :disabled="props.disabled"
      as="div"
      class="relative"
      @update:modelValue="isDropdownOpen = false"
    >
      <div class="flex items-center gap-1">
        <!-- Country Flag -->
        <img
          v-if="selectedCountry?.flag"
          :src="selectedCountry.flag"
          :alt="selectedCountry.name"
          class="w-5 h-4 object-cover rounded"
        />

        <!-- Combobox Input for Country Code -->
        <ComboboxInput
          class="w-14 focus:outline-none bg-transparent text-gray-600 cursor-pointer"
          :displayValue="displayValue"
          @change="query = $event.target.value"
          @focus="isDropdownOpen = true; isFocused = true"
          @blur="isFocused = false"
          :disabled="props.disabled"
        />

        <!-- Dropdown Arrow Button -->
        <ComboboxButton class="flex items-center" @click="isDropdownOpen = !isDropdownOpen">
          <Icon
            icon="material-symbols:arrow-drop-down"
            class="w-4 h-4 text-gray-400 transition-transform"
          />
        </ComboboxButton>
      </div>

      <!-- Dropdown Options -->
      <ComboboxOptions :class="optionsClass">
        <div
          v-if="filteredCountries.length === 0 && query !== ''"
          class="px-4 py-2 text-gray-500 text-sm"
        >
          No countries found.
        </div>

        <ComboboxOption
          v-for="country in filteredCountries"
          :key="country.code"
          :value="country"
          v-slot="{ active, selected }"
          as="template"
        >
          <li
            :class="[
              'cursor-pointer select-none py-2 px-2 transition-colors flex items-center gap-2',
              active ? 'bg-gray-100 text-gray-900' : 'text-gray-900',
              selected ? 'font-semibold' : 'font-normal'
            ]"
          >
            <img
              :src="country.flag"
              :alt="country.name"
              class="w-6 h-4 object-cover rounded"
            />
            <span class="flex-1">{{ country.name }}</span>
            <span class="text-gray-500 text-sm">{{ country.displayCode }}</span>
            <Icon
              v-if="selected"
              icon="material-symbols:check"
              class="w-5 h-5 text-primary"
            />
          </li>
        </ComboboxOption>
      </ComboboxOptions>
    </Combobox>

    <!-- Pipe Separator -->
    <div class="h-6 w-px bg-gray-300"></div>

    <!-- Phone Number Input -->
    <input
      type="tel"
      v-bind:value="phoneNumber"
      :class="inputClass"
      :placeholder="props.placeholder"
      @focus="isFocused = true"
      @blur="isFocused = false"
      @input="handlePhoneInput"
      :required="props.required"
      :disabled="props.disabled"
    />
  </div>
</div>
</template>

<style scoped>
/* Custom scrollbar for dropdown options */
.max-h-60::-webkit-scrollbar {
  width: 8px;
}

.max-h-60::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.max-h-60::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.max-h-60::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
